import Column from '@app/components/layout/Column'
import { SelectableTagModel, TagModel } from '@app/types/TagModel'
import { TagSelect } from '@app/components/core/input/tagSelect/TagSelect'
import { useTranslations } from '@i18n/useLanguage'
import { useToast } from '@app/util/hooks/useToast'
import { useCallback, useEffect, useState } from 'react'
import { FieldEditActions } from './FieldEditActions'

export type TagSelectionGroupProps<TKey extends string | number> = {
  allTags: TagModel<TKey>[]
  selectedIds: TKey[]
  onClose: VoidFunction
  onSave: (selectedIds: TKey[]) => Promise<boolean>
  mode?: 'multi' | 'single'
}

export const TagSelectionGroup = <TKey extends string | number>({
  allTags,
  selectedIds,
  onClose,
  onSave,
  mode = 'multi',
}: TagSelectionGroupProps<TKey>) => {
  const t = useTranslations().app.common
  const { showSuccessToast, showFailToast } = useToast()
  const collapsedAmountVisible = Math.max(selectedIds.length, 5)
  const [selected, setSelected] = useState<TKey[]>(selectedIds)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setSelected(selectedIds)
  }, [selectedIds])

  const onSelect = useCallback(
    (id: TKey) => {
      setSelected((prev) => {
        if (mode === 'single') {
          if (prev.length === 1 && prev[0] === id) {
            return []
          }
          return [id]
        }
        const index = prev.indexOf(id)
        return index > -1 ? prev.toSpliced(index, 1) : [...prev, id]
      })
    },
    [mode],
  )

  const handleSave = useCallback(() => {
    setIsSaving(true)
    onSave(selected)
      .then((success) => {
        if (success) {
          showSuccessToast(t.toastSuccess)
          onClose()
          return
        }
        showFailToast()
      })
      .catch(() => showFailToast())
      .finally(() => setIsSaving(false))
  }, [
    selected,
    onSave,
    showSuccessToast,
    showFailToast,
    t.toastSuccess,
    onClose,
  ])

  const tags: SelectableTagModel<TKey>[] = allTags.map((i) => ({
    ...i,
    selected: selected.includes(i.key),
  }))

  return (
    <Column style={{ alignItems: 'stretch' }}>
      <TagSelect
        tags={tags}
        collapsedAmountVisible={collapsedAmountVisible}
        onSelect={onSelect}
      />
      <FieldEditActions
        onCancel={onClose}
        onSave={handleSave}
        saveDisabled={isSaving}
      />
    </Column>
  )
}
